import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  LightbulbOutlined as TipIcon,
  FormatQuote as QuoteIcon,
} from '@mui/icons-material';
import type { WorkflowStepContent } from '../content/guideContent';

interface WorkflowStepProps {
  step: WorkflowStepContent;
  stepNumber: number;
}

const WorkflowStep: React.FC<WorkflowStepProps> = ({ step, stepNumber }) => {
  return (
    <Box id={`step-${step.id}`} sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Chip
          label={`Step ${stepNumber}`}
          color="primary"
          size="small"
          sx={{ fontWeight: 600 }}
        />
        <Typography variant="h6" component="h4" fontWeight={600}>
          {step.title.replace(`Step ${stepNumber}: `, '')}
        </Typography>
      </Box>

      <Box sx={{ pl: 2, borderLeft: 3, borderColor: 'primary.200', mb: 3 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <strong>Purpose:</strong> {step.purpose}
        </Typography>

        <Typography variant="body1" sx={{ mb: 2 }}>
          <strong>What to provide:</strong> {step.whatToProvide}
        </Typography>

        {step.keyConcepts && (
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Key concepts:</strong> {step.keyConcepts}
          </Typography>
        )}
      </Box>

      {/* Tips section */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          bgcolor: 'success.50',
          borderColor: 'success.200',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <TipIcon sx={{ color: 'success.main', fontSize: 20 }} />
          <Typography variant="subtitle2" color="success.dark" fontWeight={600}>
            Tips for Success
          </Typography>
        </Box>
        <List dense disablePadding>
          {step.tips.map((tip, index) => (
            <ListItem key={index} disablePadding sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 24 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={tip}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Example if provided */}
      {step.example && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            bgcolor: 'grey.50',
            borderColor: 'grey.300',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <QuoteIcon sx={{ color: 'grey.500', fontSize: 20, mt: 0.5 }} />
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Example
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontStyle: 'italic', color: 'text.primary' }}
              >
                {step.example}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default WorkflowStep;
